import VectorPenImage from "../../assets/images/vector-pen.svg";

const SHOW_RED_BOX = false;

let signatureIcon;
const imagePreloaded = new Promise(function (resolve) {
  /** dynamically load SVG from file, but this is async so offer a promise that the app can wait on **/
  signatureIcon = new Image();
  signatureIcon.onload = function () {
    resolve(signatureIcon);
  };
  signatureIcon.src = VectorPenImage;
});

export async function initAnchors() {
  await imagePreloaded;
}

// Generate much larger image so the text is not blurry.
export const ANCHOR_IMAGE_SCALE_UP = 4;

export const ANCHOR_PREVIEW_TEXT_FONT_FAMILY = "SpaceMono-Regular";
export const ANCHOR_PREVIEW_TEXT_FONT_SIZE = 9;
export const ANCHOR_PREVIEW_TEXT_FONT_COLOUR = "#000000";
export const ANCHOR_PREVIEW_SIGNATURE_TAB_COLOUR = "#383dfb";
export const ANCHOR_PREVIEW_SIGNATURE_TAB_WIDTH = 2; // "px"
export const ANCHOR_PREVIEW_SIGNATURE_LEFT_PADDING = 2; // "px"

export const ANCHOR_PREVIEW_SIGNATURE_HEADING_FONT_FAMILY = "helvetica-bold";
export const ANCHOR_PREVIEW_SIGNATURE_HEADING_FONT_SIZE = 7;
export const ANCHOR_PREVIEW_SIGNATURE_FOOTER_FONT_FAMILY = "SpaceMono-Regular";
export const ANCHOR_PREVIEW_SIGNATURE_FOOTER_FONT_SIZE = 6;
export const ANCHOR_PREVIEW_SIGNATURE_FONT_FAMILY = "VujahdayScript-Regular";
export const ANCHOR_PREVIEW_SIGNATURE_FONT_COLOUR = "#000000";
export const ANCHOR_PREVIEW_INITIALS_FONT_FAMILY = "VujahdayScript-Regular";
export const ANCHOR_PREVIEW_INITIALS_HEADING_FONT_FAMILY = "helvetica-bold";
export const ANCHOR_PREVIEW_INITIALS_HEADING_FONT_SIZE = 6;

export const ANCHOR_FONT_FAMILY = ANCHOR_PREVIEW_TEXT_FONT_FAMILY;
export const ANCHOR_FONT_SIZE = ANCHOR_PREVIEW_TEXT_FONT_SIZE;
export const ANCHOR_SIGNATURE_HEADING_FONT = "Roboto-Regular";

export const ANCHOR_COLOUR = {
  bg: {
    // IMPORTANT: if you change this, also update --thumbnail-tab-colour* css vars in app.css
    requester: "#78c5f5",
    signer: "#FFCC70",
    "signature request": "#c2bfbf",
  },
  text: {
    requester: "#333333",
    signer: "#333333",
    "signature request": "#333333",
  },
};

function pt2px(pt) {
  return pt * 1.33333;
}

function px2pt(pt) {
  return pt * 0.75;
}

const DEFAULT_ANCHOR_WIDTH = 250;
const DEFAULT_ANCHOR_HEIGHT = pt2px(ANCHOR_PREVIEW_TEXT_FONT_SIZE);
const SIGNATURE_ANCHOR_WIDTH = 185;
const SIGNATURE_ANCHOR_HEIGHT = 70;
const DATE_ANCHOR_WIDTH = 180;
const DATE_ANCHOR_HEIGHT = DEFAULT_ANCHOR_HEIGHT;
const NAME_ANCHOR_WIDTH = 200;
const NAME_ANCHOR_HEIGHT = DEFAULT_ANCHOR_HEIGHT;
const INITIALS_ANCHOR_WIDTH = 28;
const INITIALS_ANCHOR_HEIGHT = 30;
const UUID_ANCHOR_WIDTH = 270;
const UUID_ANCHOR_HEIGHT = DEFAULT_ANCHOR_HEIGHT;
const ROLE_ANCHOR_WIDTH = 60;
const ROLE_ANCHOR_HEIGHT = DEFAULT_ANCHOR_HEIGHT;
const IP_ANCHOR_WIDTH = 110;
const IP_ANCHOR_HEIGHT = DEFAULT_ANCHOR_HEIGHT;
const CHAT_SEQ_WIDTH = 70;
const CHAT_SEQ_HEIGHT = DEFAULT_ANCHOR_HEIGHT;

const CALCULATED_FIELDS = {
  NOW: 0,
  FILENAME: 1,
};

const ANCHOR_TYPE = {
  TEXT: 0, // text-based anchor where users may be allowed to change font name and font size
  SIGNATURE: 1, // signature-based anchor where users may be allowed to change anchor size
  INITIALS: 2, // initials-based anchor where users may be allowed to change anchor size
};

const ANCHOR_CONSTRAINTS = {
  [ANCHOR_TYPE.SIGNATURE]: {
    minWidth: SIGNATURE_ANCHOR_WIDTH * 0.5,
    minHeight: SIGNATURE_ANCHOR_HEIGHT * 0.5,
    maxWidth: SIGNATURE_ANCHOR_WIDTH * 1.5,
    maxHeight: SIGNATURE_ANCHOR_HEIGHT * 1.5,
  },
  [ANCHOR_TYPE.INITIALS]: {
    minWidth: INITIALS_ANCHOR_WIDTH * 0.5,
    minHeight: INITIALS_ANCHOR_HEIGHT * 0.5,
    maxWidth: INITIALS_ANCHOR_WIDTH * 1.5,
    maxHeight: INITIALS_ANCHOR_HEIGHT * 1.5,
  },
};

export const ANCHORS = {
  requester: {
    main: [
      {
        label: "Name",
        icon: "bi-person",
        anchor: "[[r|name]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Full name",
        description: "Requester name",
        longDescription: "Name of the person sending the signature request.",
        width: NAME_ANCHOR_WIDTH,
        height: NAME_ANCHOR_HEIGHT,
        previewData: "Larry Jones",
        mightExceedBounds: true,
      },
      {
        label: "Signature",
        icon: "bi-vector-pen",
        anchor: "[[r|signature]]",
        anchorType: ANCHOR_TYPE.SIGNATURE,
        anchorText: "Signature",
        description: "Requester signature",
        longDescription: "Signature of the person sending the request.",
        width: SIGNATURE_ANCHOR_WIDTH,
        height: SIGNATURE_ANCHOR_HEIGHT,
        previewData: "Larry Jones",
        mightExceedBounds: true,
      },
      {
        label: "Initials",
        icon: "bi-alphabet",
        anchor: "[[r|initials]]",
        anchorType: ANCHOR_TYPE.INITIALS,
        anchorText: "Initials",
        description: "Requester initials",
        longDescription: "Initials of the person sending the request.",
        width: INITIALS_ANCHOR_WIDTH,
        height: INITIALS_ANCHOR_HEIGHT,
        previewData: "LJ",
        mightExceedBounds: false,
      },
      {
        label: "Signature Date",
        icon: "bi-calendar-date",
        anchor: "[[r|signedAt]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Signature date",
        description: "Requester signature date",
        longDescription:
          "Date and time the signature of the sender is added to the document.",
        width: DATE_ANCHOR_WIDTH,
        height: DATE_ANCHOR_HEIGHT,
        previewData: CALCULATED_FIELDS.NOW,
        mightExceedBounds: false,
      },
    ],
    more: [
      {
        label: "Email",
        icon: "bi-envelope",
        anchor: "[[r|email]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Email",
        description: "Requester email",
        longDescription: "Email of the person sending the request.",
        width: DEFAULT_ANCHOR_WIDTH,
        height: DEFAULT_ANCHOR_HEIGHT,
        previewData: "larry.jones@outlook.com",
        mightExceedBounds: true,
      },
      {
        label: "Role (staff/client)",
        icon: "bi-person-vcard",
        anchor: "[[r|role]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Role",
        description: "Requester role",
        longDescription:
          "The role (staff or client) of the person sending the request.",
        width: ROLE_ANCHOR_WIDTH,
        height: ROLE_ANCHOR_HEIGHT,
        previewData: "Staff",
      },
      {
        label: "Organisation Name",
        icon: "bi-building",
        anchor: "[[r|entityName]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Organisation name",
        description: "Requester organisation name",
        longDescription:
          "Organisation name of the person sending the signature request.",
        width: DEFAULT_ANCHOR_WIDTH,
        height: DEFAULT_ANCHOR_HEIGHT,
        previewData: "Sample Organisation",
        mightExceedBounds: true,
      },
      {
        label: "User ID",
        icon: "bi-person-fill-gear",
        anchor: "[[r|userId]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "User ID",
        description: "Requester user ID",
        longDescription:
          "Unique Qwil identifier of the person sending the request.",
        width: UUID_ANCHOR_WIDTH,
        height: UUID_ANCHOR_HEIGHT,
        previewData: "e640a12b-d039-4e69-a29d-73de4622ad54",
        mightExceedBounds: false,
      },
      {
        label: "Signature ID",
        icon: "bi-upc",
        anchor: "[[r|signatureId]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Signature ID",
        description: "Requester signature ID",
        longDescription:
          "Unique identifier of the signature of the person sending the request.",
        width: UUID_ANCHOR_WIDTH,
        height: UUID_ANCHOR_HEIGHT,
        previewData: "999c470d-ddb5-4657-b3d6-f342bfaac796",
        mightExceedBounds: false,
      },
      {
        label: "Organisation ID",
        icon: "bi-building-fill-gear",
        anchor: "[[r|entityId]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Organisation ID",
        description: "Requester organisation ID",
        longDescription:
          "Unique Qwil identifier of the organisation of the person sending the request.",
        width: UUID_ANCHOR_WIDTH,
        height: UUID_ANCHOR_HEIGHT,
        previewData: "db87679c-0652-46bd-9566-0c99abb97fe7",
        mightExceedBounds: false,
      },
      {
        label: "IP Address",
        icon: "bi-router",
        anchor: "[[r|ipAddress]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "IP address",
        description: "Requester IP address",
        longDescription: "IP address of the person sending the request.",
        width: IP_ANCHOR_WIDTH,
        height: IP_ANCHOR_HEIGHT,
        previewData: "123.235.56.143",
        mightExceedBounds: false,
      },
    ],
  },
  signer: {
    main: [
      {
        label: "Name",
        icon: "bi-person",
        anchor: "[[s|name]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Full name",
        description: "Signer name",
        longDescription: "Name of the request recipient",
        width: NAME_ANCHOR_WIDTH,
        height: NAME_ANCHOR_HEIGHT,
        previewData: "Laurence Simmons",
        mightExceedBounds: true,
      },
      {
        label: "Signature",
        icon: "bi-vector-pen",
        anchor: "[[s|signature]]",
        anchorType: ANCHOR_TYPE.SIGNATURE,
        anchorText: "Signature",
        description: "Signer signature",
        longDescription: "Signature of the request recipient.",
        width: SIGNATURE_ANCHOR_WIDTH,
        height: SIGNATURE_ANCHOR_HEIGHT,
        previewData: "Laurence Simmons",
        mightExceedBounds: true,
      },
      {
        label: "Initials",
        icon: "bi-alphabet",
        anchor: "[[s|initials]]",
        anchorType: ANCHOR_TYPE.INITIALS,
        anchorText: "Initials",
        description: "Signer initials",
        longDescription: "Initials of the request recipient.",
        width: INITIALS_ANCHOR_WIDTH,
        height: INITIALS_ANCHOR_HEIGHT,
        previewData: "LS",
        mightExceedBounds: false,
      },
      {
        label: "Signature Date",
        icon: "bi-calendar-date",
        anchor: "[[s|signedAt]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Signature date",
        description: "Signer signature date",
        longDescription:
          "Date and time the signature of the request recipient is added to the document.",
        width: DATE_ANCHOR_WIDTH,
        height: DATE_ANCHOR_HEIGHT,
        previewData: CALCULATED_FIELDS.NOW,
        mightExceedBounds: false,
      },
    ],
    more: [
      {
        label: "Email",
        icon: "bi-envelope",
        anchor: "[[s|email]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Email",
        description: "Signer email",
        longDescription: "Email of the request recipient",
        width: DEFAULT_ANCHOR_WIDTH,
        height: DEFAULT_ANCHOR_HEIGHT,
        previewData: "laurence.simmons@outlook.com",
        mightExceedBounds: true,
      },
      {
        label: "Role (staff/client)",
        icon: "bi-person-vcard",
        anchor: "[[s|role]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Role",
        description: "Signer role",
        longDescription: "The role (staff or client) of the request recipient.",
        width: ROLE_ANCHOR_WIDTH,
        height: ROLE_ANCHOR_HEIGHT,
        previewData: "Client",
        mightExceedBounds: false,
      },
      {
        label: "Organisation Name",
        icon: "bi-building",
        anchor: "[[s|entityName]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Organisation name",
        description: "Signer organisation name",
        longDescription: "Organisation name of the request recipient.",
        width: DEFAULT_ANCHOR_WIDTH,
        height: DEFAULT_ANCHOR_HEIGHT,
        previewData: "Sample Organisation",
        mightExceedBounds: true,
      },
      {
        label: "User ID",
        icon: "bi-person-fill-gear",
        anchor: "[[s|userId]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "User ID",
        description: "Signer user ID",
        longDescription: "Unique Qwil identifier of the request recipient.",
        width: UUID_ANCHOR_WIDTH,
        height: UUID_ANCHOR_HEIGHT,
        previewData: "a640a12b-d039-4e69-a29d-73de4622ad54",
        mightExceedBounds: false,
      },
      {
        label: "Signature ID",
        icon: "bi-upc",
        anchor: "[[s|signatureId]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Signature ID",
        description: "Signer signature ID",
        longDescription:
          "Unique identifier of the signature of the request recipient.",
        width: UUID_ANCHOR_WIDTH,
        height: UUID_ANCHOR_HEIGHT,
        previewData: "a99c470d-ddb5-4657-b3d6-f342bfaac796",
        mightExceedBounds: false,
      },
      {
        label: "Organisation ID",
        icon: "bi-building-fill-gear",
        anchor: "[[s|entityId]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Organisation ID",
        description: "Signer organisation ID",
        longDescription:
          "Unique Qwil identifier of the organisation of the request recipient.",
        width: UUID_ANCHOR_WIDTH,
        height: UUID_ANCHOR_HEIGHT,
        previewData: "db87679c-0652-46bd-9566-0c99abb97fe7",
        mightExceedBounds: false,
      },
      {
        label: "IP Address",
        icon: "bi-router",
        anchor: "[[s|ipAddress]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "IP address",
        description: "Signer IP address",
        longDescription: "IP address of the recipient signing the request.",
        width: IP_ANCHOR_WIDTH,
        height: IP_ANCHOR_HEIGHT,
        previewData: "122.245.56.113",
        mightExceedBounds: false,
      },
    ],
  },
  "signature request": {
    main: [
      {
        label: "Request Date",
        icon: "bi-calendar-date",
        anchor: "[[SR|createdAt]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Request date",
        description: "Request date",
        longDescription: "Date and time the signature request was sent.",
        width: DATE_ANCHOR_WIDTH,
        height: DATE_ANCHOR_HEIGHT,
        previewData: CALCULATED_FIELDS.NOW,
        mightExceedBounds: false,
      },
      {
        label: "Completion Date",
        icon: "bi-calendar-date",
        anchor: "[[SR|completedAt]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Completion date",
        description: "Completion date",
        longDescription:
          "Date and time the document was signed by the request recipient.",
        width: DATE_ANCHOR_WIDTH,
        height: DATE_ANCHOR_HEIGHT,
        previewData: CALCULATED_FIELDS.NOW,
        mightExceedBounds: false,
      },
    ],
    more: [
      {
        label: "Request ID",
        icon: "bi-gear-wide",
        anchor: "[[SR|id]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Request ID",
        description: "Request ID",
        longDescription: "Unique Qwil identifier of the signature request.",
        width: UUID_ANCHOR_WIDTH,
        height: UUID_ANCHOR_HEIGHT,
        previewData: "8643b4ef-c868-4dd4-986a-1b9e1a7a0abd",
        mightExceedBounds: false,
      },
      {
        label: "File Name",
        icon: "bi-filetype-pdf",
        anchor: "[[SR|fileName]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "File name",
        description: "File name",
        longDescription:
          "Name of the document included in the signature request.",
        width: DEFAULT_ANCHOR_WIDTH,
        height: DEFAULT_ANCHOR_HEIGHT,
        previewData: CALCULATED_FIELDS.FILENAME,
        mightExceedBounds: true,
      },
      {
        label: "File ID",
        icon: "bi-gear-wide",
        anchor: "[[SR|fileId]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "File ID",
        description: "File ID",
        longDescription:
          "Unique Qwil identifier of the document included in the signature request.",
        width: UUID_ANCHOR_WIDTH,
        height: UUID_ANCHOR_HEIGHT,
        previewData: "a22a1a08-0c95-4214-9410-fda6d6fd5926",
        mightExceedBounds: false,
      },
      {
        label: "Chat Title",
        icon: "bi-chat-left-text",
        anchor: "[[SR|chatTitle]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Chat title",
        description: "Chat title",
        longDescription:
          "Title of the chat where the signature request was sent.",
        width: 300,
        height: DEFAULT_ANCHOR_HEIGHT,
        previewData: "Sample Chat Title",
        mightExceedBounds: true,
      },
      {
        label: "Chat ID",
        icon: "bi-gear-wide",
        anchor: "[[SR|chatId]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Chat ID",
        description: "Chat ID",
        longDescription:
          "Unique Qwil identifier of the chat where the signature request was sent.",
        width: UUID_ANCHOR_WIDTH,
        height: UUID_ANCHOR_HEIGHT,
        previewData: "630186bc-dc6f-4624-aad6-3bb8eb631fc4",
        mightExceedBounds: false,
      },
      {
        label: "Chat Sequence",
        icon: "bi-gear-wide",
        anchor: "[[SR|chatSeq]]",
        anchorType: ANCHOR_TYPE.TEXT,
        anchorText: "Chat seq.",
        description: "Chat sequence",
        longDescription:
          "Message sequence of the signature request. This sequence along with the Chat ID uniquely identifies the " +
          "message that contains the signature request.",
        width: CHAT_SEQ_WIDTH,
        height: CHAT_SEQ_HEIGHT,
        previewData: "12",
        mightExceedBounds: false,
      },
    ],
  },
};

function get_flattened_anchor_map() {
  // Expect this to raise if anchor names are not unique

  const anchors = {};

  function gather_from(category, section) {
    ANCHORS[category][section].forEach(function (obj) {
      if (obj.anchor in anchors) {
        throw new Error(`Duplicate definition for "${obj.anchor}" anchor`);
      }

      anchors[obj.anchor] = {
        data: obj,
        category: category,
        section: section,
      };
    });
  }

  gather_from("requester", "main");
  gather_from("requester", "more");
  gather_from("signer", "main");
  gather_from("signer", "more");
  gather_from("signature request", "main");
  gather_from("signature request", "more");

  return anchors;
}

export const ANCHOR_MAP = get_flattened_anchor_map();

export function isTextBasedAnchor(anchorName) {
  return ANCHOR_MAP[anchorName].data.anchorType === ANCHOR_TYPE.TEXT;
}

export function isSizeBasedAnchor(anchorName) {
  return !isTextBasedAnchor(anchorName);
}

function getPreviewText(anchor, filename = "uploaded_file.pdf") {
  if (anchor.previewData === CALCULATED_FIELDS.NOW) {
    // no built-in way to apply "%d %b %Y %H:%M:%S UTC" formatting, so we hack the closest repr
    // "Sat, 07 Oct 2023 09:51:18 GMT" --> "07 Oct 2023 09:51:41 UTC"
    let dateString = new Date().toUTCString();
    dateString = dateString.replace("GMT", "UTC");
    dateString = dateString.split(", ")[1];
    return dateString;
  } else if (anchor.previewData === CALCULATED_FIELDS.FILENAME) {
    return filename;
  } else {
    return anchor.previewData;
  }
}

export function makeAnchorPreviewElement(anchorName, width, height) {
  const element = document.createElement("div");
  element.style.width = width + "px";
  element.style.height = height + "px";
  element.style.display = "flex";

  const img = document.createElement("img");
  img.style.width = "100%";
  img.style.height = "100%";
  img.style.maxWidth = "100%";
  img.style.maxHeight = "100%";
  img.setAttribute("src", makePreviewImage(anchorName));
  element.appendChild(img);

  return element;
}

const _anchorImageCache = {};

function _makePreviewImage(anchorName) {
  const anchor = ANCHOR_MAP[anchorName];

  if (!anchor) {
    throw new Error(`Unknown anchor "${anchorName}"`);
  }

  if (anchor.data.anchorType === ANCHOR_TYPE.TEXT) {
    return _makeTextPreviewImage(anchor);
  } else {
    return _makeSignaturePreviewImage(anchor);
  }
}

function _makeTextPreviewImage(anchor) {
  // @nicolas, for BE, you can ignore S. In FE we artificially scale it up so that it looks OK when zoom
  const S = ANCHOR_IMAGE_SCALE_UP;
  const previewText = getPreviewText(anchor.data);
  const fontFamily = ANCHOR_PREVIEW_TEXT_FONT_FAMILY; // "pt"
  const fontColour = ANCHOR_PREVIEW_TEXT_FONT_COLOUR;

  const fontSize = ANCHOR_PREVIEW_TEXT_FONT_SIZE * S;
  const width = anchor.data.width * S; // artificial constraint for cosmetic purposes. On BE, text can exceed boundary.
  const height = pt2px(fontSize);

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  canvas.width = width;
  canvas.height = height;

  context.font = `${fontSize}pt ${fontFamily}`;
  context.fillStyle = fontColour;
  context.textAlign = "left";

  context.fillText(previewText, 0, fontSize);

  return getImageUrlAndFreeCanvas(canvas);
}

function _makeSignaturePreviewImage(anchor) {
  // @nicolas, for BE, you can treat S as the ratio of USER_WIDTH:DEFAULT_WIDTH
  const S = ANCHOR_IMAGE_SCALE_UP;

  const width = anchor.data.width * S;
  const height = anchor.data.height * S;

  const signatureText = getPreviewText(anchor.data);
  const footerText = anchor.data.anchor.startsWith("[[r|")
    ? "A99C470DDDB5465..."
    : "999C470DDDB5465...";
  const fontColour = ANCHOR_PREVIEW_SIGNATURE_FONT_COLOUR;

  const verticalBarColour = ANCHOR_PREVIEW_SIGNATURE_TAB_COLOUR;
  const verticalBarWidth = ANCHOR_PREVIEW_SIGNATURE_TAB_WIDTH * S; // "px"

  const leftPadding = ANCHOR_PREVIEW_SIGNATURE_LEFT_PADDING * S; // "px"
  const signaturePaddingY = 5; // "px"

  let footerFontFamily;
  let footerFontSize;
  let headingText;
  let headingFontSize;
  let headingFontFamily;
  let signatureFontFamily;
  let fontRatio; // this determines how much of vertical white space is used for signature text height
  if (anchor.data.anchorType === ANCHOR_TYPE.SIGNATURE) {
    headingText = "Signature preview:";
    fontRatio = 0.49;
    headingFontFamily = ANCHOR_PREVIEW_SIGNATURE_HEADING_FONT_FAMILY;
    headingFontSize = ANCHOR_PREVIEW_SIGNATURE_HEADING_FONT_SIZE * S; // "pt"
    footerFontFamily = ANCHOR_PREVIEW_SIGNATURE_FOOTER_FONT_FAMILY;
    footerFontSize = ANCHOR_PREVIEW_SIGNATURE_FOOTER_FONT_SIZE * S; // "pt"
    signatureFontFamily = ANCHOR_PREVIEW_SIGNATURE_FONT_FAMILY;
  } else {
    headingText = "QS:";
    fontRatio = 0.75;
    headingFontFamily = ANCHOR_PREVIEW_INITIALS_HEADING_FONT_FAMILY;
    headingFontSize = ANCHOR_PREVIEW_INITIALS_HEADING_FONT_SIZE * S; // "pt"
    // no footer for initials
    footerFontSize = 0;
    signatureFontFamily = ANCHOR_PREVIEW_INITIALS_FONT_FAMILY;
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  canvas.width = width;
  canvas.height = height;

  // Draw left vertical bar
  context.fillStyle = verticalBarColour;
  context.fillRect(0, 0, verticalBarWidth, height);

  // From now on we draw from same X offset using same colour
  let y;
  const x = verticalBarWidth + leftPadding;
  context.fillStyle = fontColour;
  context.textAlign = "left";

  // Draw heading
  context.font = `${headingFontSize}pt ${headingFontFamily}`;
  context.fillText(headingText, x, headingFontSize);

  // Maybe draw footer
  if (footerFontFamily) {
    context.font = `${footerFontSize}pt ${footerFontFamily}`;
    y = height;
    context.fillText(footerText, x, y);
  }

  // Draw signature
  const bottomOfHeadingText = pt2px(headingFontSize);
  const topOfFooterText = height - pt2px(footerFontSize); // footerFontSize would be 0 for initials
  const signatureBoxHeight =
    topOfFooterText - bottomOfHeadingText - 2 * signaturePaddingY;

  const spacingRatio = (1 - fontRatio) / 2;
  // For image-based signatures, signatureBoxHeight will be image height, and the width a ratio of that.
  // For text-based signatures like this preview, we use  a % of the space, centered
  const signatureFontSize = px2pt(signatureBoxHeight) * fontRatio;
  const signaturePaddingTop =
    signatureBoxHeight * spacingRatio + signaturePaddingY;

  if (SHOW_RED_BOX) {
    const signatureImageRatio =
      anchor.data.anchorType === ANCHOR_TYPE.SIGNATURE ? 3.5 : 1;
    context.save();
    context.fillStyle = "red";
    context.fillRect(
      x,
      bottomOfHeadingText + signaturePaddingY,
      signatureBoxHeight * signatureImageRatio,
      signatureBoxHeight,
    );
    context.restore();
  }

  context.font = `${signatureFontSize}pt ${signatureFontFamily}`;
  y = bottomOfHeadingText + signaturePaddingTop + signatureFontSize;
  context.fillText(signatureText, x, y);

  return getImageUrlAndFreeCanvas(canvas);
}

function _makeAnchorImage(anchorName) {
  const S = ANCHOR_IMAGE_SCALE_UP;
  const anchor = ANCHOR_MAP[anchorName];

  if (!anchor) {
    throw new Error(`Unknown anchor "${anchorName}"`);
  }

  const { data, category } = anchor;
  const { width, height, anchorText, anchorType } = data;

  const scaledWidth = width * S;
  const scaledHeight = height * S;
  const maxWidth = scaledWidth;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  canvas.width = scaledWidth;
  canvas.height = scaledHeight;

  // fill background
  context.fillStyle = ANCHOR_COLOUR.bg[category];
  context.fillRect(0, 0, scaledWidth, scaledHeight);

  // write content
  context.fillStyle = ANCHOR_COLOUR.text[category];
  context.textAlign = "left";

  if (anchorType === ANCHOR_TYPE.TEXT) {
    context.font = ANCHOR_FONT_SIZE * S + "pt " + ANCHOR_FONT_FAMILY;
    context.fillText(anchorText, 0, ANCHOR_FONT_SIZE * S);
  } else {
    let text, fontSize, iconSize;
    if (anchorType === ANCHOR_TYPE.SIGNATURE) {
      text = "Signature";
      fontSize = 10;
      iconSize = 170;
    } else {
      text = "Initials";
      fontSize = 6;
      iconSize = 70;
    }

    // draw heading text
    const lineHeight = fontSize * 1.3 * S;
    const x = 0;
    const y = lineHeight;
    context.font = fontSize * S + "pt " + ANCHOR_SIGNATURE_HEADING_FONT;
    wrapAndFillText(context, text, x, y, maxWidth, lineHeight, true);

    // draw icon
    const marginBetweenHeadingAndIcon = 5 * S;
    const x2 = (maxWidth - iconSize) / 2; // middle
    const y2 = height * S - y - iconSize + marginBetweenHeadingAndIcon;
    context.drawImage(signatureIcon, x2, y2, iconSize, iconSize);
  }

  return getImageUrlAndFreeCanvas(canvas);
}

function getImageUrlAndFreeCanvas(canvas) {
  const dataUrl = canvas.toDataURL("image/png");
  // Zeroing the width and height causes Firefox to release graphics
  // resources immediately, which can greatly reduce memory consumption.
  canvas.width = 0;
  canvas.height = 0;

  return dataUrl;
}

export function makeAnchorImage(anchorName, flushCache) {
  if (flushCache || !(anchorName in _anchorImageCache)) {
    _anchorImageCache[anchorName] = _makeAnchorImage(anchorName);
  }
  return _anchorImageCache[anchorName];
}

export function makePreviewImage(anchorName, flushCache) {
  const key = "preview_" + anchorName;
  if (flushCache || !(key in _anchorImageCache)) {
    _anchorImageCache[key] = _makePreviewImage(anchorName);
  }
  return _anchorImageCache[key];
}

export function getAnchorLongDescription(anchorName) {
  return ANCHOR_MAP[anchorName].data.longDescription;
}

export function getAnchorIcon(anchorName) {
  return ANCHOR_MAP[anchorName].data.icon;
}

export function anchorContentMightExceedBounds(anchorName) {
  return ANCHOR_MAP[anchorName].data.mightExceedBounds === true;
}

export function getAnchorDefaultDimensions(anchorName) {
  const data = ANCHOR_MAP[anchorName].data;
  return {
    width: data.width,
    height: data.height,
  };
}

export function getAnchorCategory(anchorName) {
  return ANCHOR_MAP[anchorName].category;
}

export function getCategoryColour(category) {
  return {
    text: ANCHOR_COLOUR.text[category],
    bg: ANCHOR_COLOUR.bg[category],
  };
}

export function getResizableAnchorConstraints(anchorName) {
  return ANCHOR_CONSTRAINTS[ANCHOR_MAP[anchorName].data.anchorType];
}

function wrapAndFillText(context, text, x, y, maxWidth, lineHeight, centered) {
  /** context.fillText does not warp text, so we need to implement it ourselves. **/
  const words = text.split(" ");
  const lines = [];
  let line = "";

  for (let i = 0; i < words.length; i++) {
    let currentWord = words[i];
    let currentWordWidth = context.measureText(currentWord).width;
    if (currentWordWidth > maxWidth) {
      // this word itself is longer than maxWidth. Write it in a new line (which may exceed width)
      if (line) {
        lines.push(line);
        line = "";
      }
      lines.push(currentWord);
    } else {
      // measure how long text would be with currentWord added on
      let maybeLine = `${line} ${currentWord}`.trim();
      let maybeLineWidth = context.measureText(maybeLine).width;
      if (maybeLineWidth > maxWidth) {
        lines.push(line);
        line = currentWord;
      } else {
        line = maybeLine;
      }
    }
  }

  if (line) {
    lines.push(line);
  }

  // we need to shift starting point upwards to make space
  let y2 = y - (lineHeight / 2) * (lines.length - 1);

  for (let i = 0; i < lines.length; i++) {
    let shift = centered
      ? (maxWidth - context.measureText(lines[i]).width) / 2
      : 0;
    context.fillText(lines[i], x + shift, y2);
    y2 += lineHeight;
  }
}
